export type WallOwner =
  | { kind: "human"; email: string }
  | { kind: "automation"; project?: string; job?: string }
  | { kind: "unassigned" };

/** GitLab project paths are accepted and persisted at this wire boundary. */
export const WALL_OWNER_PROJECT_MAX_LENGTH = 500;

export const DISPLAY_PHASES = [
  "checked_out",
  "downloading",
  "waiting_install_slot",
  "installing_main",
  "installing_test",
  "device_setup",
  "testing",
  "cleanup_apps",
  "cleanup_animations",
  "cleanup_notifications",
  "available",
  "maintenance",
  "offline"
] as const;

export type DisplayPhase = (typeof DISPLAY_PHASES)[number];

export type IdentifyOutcome = "accepted" | "busy" | "offline" | "unsupported";
export type IdentifyAdmission =
  | { outcome: "accepted"; requestId: string; generation: number; expiresAt: string }
  | { outcome: "busy" | "offline" | "unsupported" };

export interface IdentifyOverlay {
  requestId: string;
  revision: number;
  serial: string;
  model: string;
  osVersion: string;
  wallLocation?: string;
  expiresAt: string;
  visibleUntil: string | null;
}

export interface IdentifyDirective {
  requestId: string;
  deviceId: string;
  sessionId: string;
  generation: number;
  revision: number;
  serial: string;
  expiresAt: string;
}

export interface IdentifyDeviceObservation {
  serial: string;
  connected: boolean;
  identify_capability?: { version: number; companion_version: string };
}

export interface WallSnapshot {
  sessionId: string;
  generation: number;
  revision: number;
  serial: string;
  /** Hive device UUID; non-secret, shown on the idle status page. */
  deviceId: string;
  /** Hostname of the host the device is attached to. */
  hostName: string | null;
  model: string | null;
  osVersion: string | null;
  owner: WallOwner;
  app: string | null;
  runId: string | null;
  phase: DisplayPhase;
  updatedAt: string;
  expiresAt: string;
  download: { bytes: number; total: number | null } | null;
  identify?: IdentifyOverlay;
}

export interface DisplayProgress {
  sessionId: string;
  generation: number;
  sequence: number;
  phase: DisplayPhase;
  download: WallSnapshot["download"];
}

/**
 * Identifies the exact lease the caller observed when it committed the
 * ownership-transition it wants reflected on the wall. A server-side advance
 * must be scoped to this precise claim/session/generation rather than
 * whatever session is currently active for the device, or a delayed advance
 * from a superseded claim can mutate a device's newer lease.
 */
export interface DisplayAdvanceProvenance {
  claimId: string;
  sessionId: string;
  generation: number;
}

export type DisplayBoundary = "lease" | "cleanup" | "maintenance" | "available";

export interface DisplayDirective {
  sessionId: string;
  generation: number;
  serial: string;
  expiresAt: string;
  boundary: DisplayBoundary;
}

export interface DisplayAccess {
  snapshot: WallSnapshot;
  capability: string;
  lastSequence: number;
}

export type ProgressSink = (
  phase: DisplayPhase,
  download?: WallSnapshot["download"]
) => void;
